/**
 * The `enrichment.translations` map: the 15-locale table, blank detection, and
 * the diff that decides what `PUT /{x}/enrichment-translations` actually sends.
 *
 * The API's merge semantics drive every rule here (design B.2):
 *
 * - a locale PRESENT in the request **replaces that whole block** — an omitted
 *   member is a cleared member, which is why the diff is per-locale and never
 *   per-field;
 * - a locale ABSENT is untouched;
 * - `"translations": {}` is a 200 no-op, and `null` is a 400, so a patch with
 *   no locale in it is not sent at all;
 * - there is **no delete**. A locale that was stored and is now blank is sent
 *   as `{}` — the only blanking operation the API offers.
 */
import { z } from "zod";

import { LOCALES, TRANSLATION_LOCALES, type TranslationLocale } from "./config";

export { LOCALES, TRANSLATION_LOCALES };
export type { TranslationLocale };

/** The seven members of a locale block, in DTO order (`EnrichmentTranslationDbDto`). */
export const TRANSLATION_BLOCK_KEYS = [
  "seoTitle",
  "shortDescription",
  "about",
  "overview",
  "faq",
  "keywords",
  "relatedSearches",
] as const;

export interface TranslationFaqItem {
  question: string;
  answer: string;
}

/** One locale block. Every member nullable, exactly as the DTO declares it. */
export interface TranslationBlock {
  seoTitle?: string | null;
  shortDescription?: string | null;
  about?: string | null;
  overview?: string | null;
  faq?: TranslationFaqItem[] | null;
  keywords?: string[] | null;
  relatedSearches?: string[] | null;
}

export type TranslationMap = Partial<
  Record<TranslationLocale, TranslationBlock | null | undefined>
>;

/** One empty locale block. */
export function emptyTranslation(): TranslationBlock {
  return {};
}

/** A string with something in it. */
function isFilled(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function isFilledArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/**
 * Whether a block carries nothing a curator would miss.
 *
 * `faq` counts through the same row filter the patch builder uses, so a block
 * holding one blank row is empty — it would be sent as `{}` anyway.
 */
export function isTranslationEmpty(
  block: TranslationBlock | null | undefined,
): boolean {
  if (!block || typeof block !== "object") return true;
  if (
    isFilled(block.seoTitle) ||
    isFilled(block.shortDescription) ||
    isFilled(block.about) ||
    isFilled(block.overview) ||
    isFilledArray(block.keywords) ||
    isFilledArray(block.relatedSearches)
  ) {
    return false;
  }
  return translationFaqRows(block.faq).length === 0;
}

/**
 * The FAQ rows the API accepts.
 *
 * `question` and `answer` are both `required` on `FaqItemDbDto`, so a
 * half-filled row is a 400 for the whole request. `["", " "]`-style blanks and
 * rows missing either member are dropped here (design B.5).
 */
export function translationFaqRows(
  faq: TranslationBlock["faq"],
): TranslationFaqItem[] {
  if (!Array.isArray(faq)) return [];
  return faq.filter(
    (row): row is TranslationFaqItem =>
      !!row && isFilled(row.question) && isFilled(row.answer),
  );
}

/**
 * The API-shaped body of one locale block: only the members that carry
 * something, `faq` filtered to complete rows.
 *
 * An omitted member is a cleared member on a whole-locale replace, so a blank
 * member must NOT be sent as `""`/`[]` — it must be absent.
 */
export function translationBlockBody(
  block: TranslationBlock | null | undefined,
): TranslationBlock {
  const body: TranslationBlock = {};
  if (!block || typeof block !== "object") return body;

  if (isFilled(block.seoTitle)) body.seoTitle = block.seoTitle;
  if (isFilled(block.shortDescription))
    body.shortDescription = block.shortDescription;
  if (isFilled(block.about)) body.about = block.about;
  if (isFilled(block.overview)) body.overview = block.overview;
  const faq = translationFaqRows(block.faq);
  if (faq.length > 0) body.faq = faq;
  if (isFilledArray(block.keywords)) body.keywords = block.keywords;
  if (isFilledArray(block.relatedSearches))
    body.relatedSearches = block.relatedSearches;

  return body;
}

/** Structural equality over the API-shaped form of two blocks. */
function sameBlock(
  a: TranslationBlock | null | undefined,
  b: TranslationBlock | null | undefined,
): boolean {
  return (
    JSON.stringify(translationBlockBody(a)) ===
    JSON.stringify(translationBlockBody(b))
  );
}

/**
 * The locales whose draft differs from what was stored.
 *
 * In the API's own locale order. Two rules beyond "differs":
 *
 * - a locale that was **never stored** and is still blank is dropped, so
 *   "Add locale -> fr" followed by typing nothing writes nothing;
 * - a locale that **was stored** and is now blank IS returned, because sending
 *   `{}` is the only blanking operation the API offers (D2: no delete).
 */
export function dirtyLocales(
  stored: TranslationMap | null | undefined,
  draft: TranslationMap | null | undefined,
): TranslationLocale[] {
  const result: TranslationLocale[] = [];
  for (const locale of TRANSLATION_LOCALES) {
    const storedBlock = stored?.[locale];
    const draftBlock = draft?.[locale];

    if (storedBlock === undefined && isTranslationEmpty(draftBlock)) continue;
    if (sameBlock(storedBlock, draftBlock)) continue;
    result.push(locale);
  }
  return result;
}

/**
 * The `translations` value of the request body: the dirty locales only, each
 * already shaped for the API.
 *
 * Absent locales are untouched by construction — a locale this map does not
 * name is never written, and never deleted.
 */
export function translationsPatchBody(
  stored: TranslationMap | null | undefined,
  draft: TranslationMap | null | undefined,
): Partial<Record<TranslationLocale, TranslationBlock>> {
  const patch: Partial<Record<TranslationLocale, TranslationBlock>> = {};
  for (const locale of dirtyLocales(stored, draft)) {
    patch[locale] = translationBlockBody(draft?.[locale]);
  }
  return patch;
}

/** How many locales of the 15 carry content — the picker's "4 of 15" counter. */
export function nonEmptyLocaleCount(
  draft: TranslationMap | null | undefined,
): number {
  return TRANSLATION_LOCALES.filter(
    (locale) => !isTranslationEmpty(draft?.[locale]),
  ).length;
}

/** `"French (fr)"` — the picker's option label. */
export function localeOptionLabel(locale: TranslationLocale): string {
  const label = LOCALES.find((entry) => entry.code === locale)?.label ?? locale;
  return `${label} (${locale})`;
}

/** The locales the picker may still offer: not already in the draft. */
export function availableLocales(
  draft: TranslationMap | null | undefined,
): TranslationLocale[] {
  return TRANSLATION_LOCALES.filter((locale) => draft?.[locale] === undefined);
}

// ---------------------------------------------------------------------------
// The request body's schema
// ---------------------------------------------------------------------------

/**
 * One locale block, as the API binds it. Members are optional, but a block that
 * is *present* must be an object: `"<locale>": null` is a 400.
 * Deliberately no `transliterations` member (design D14).
 */
export const translationBlockSchema = z
  .object({
    seoTitle: z.string().nullish(),
    shortDescription: z.string().nullish(),
    about: z.string().nullish(),
    overview: z.string().nullish(),
    faq: z
      .array(
        z.object({
          question: z.string(),
          answer: z.string(),
        }),
      )
      .nullish(),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
  })
  .strict();

/** The translations map: the 15 locales, no others, `{}` allowed, `null` not. */
export const translationsMapSchema = z
  .object(
    Object.fromEntries(
      TRANSLATION_LOCALES.map((locale) => [
        locale,
        translationBlockSchema.optional(),
      ]),
    ),
  )
  .strict();

/** `PUT /{x}/enrichment-translations` — both members are `required`. */
export const translationsRequestBodySchema = z.object({
  id: z.string(),
  translations: translationsMapSchema,
});

export type TranslationsRequestBody = z.infer<
  typeof translationsRequestBodySchema
>;

export type TranslationBlockBody = z.infer<typeof translationBlockSchema>;
